import _extends from "@babel/runtime/helpers/extends";
/* eslint @atlaskit/design-system/no-html-anchor: 0 */
import React, { Fragment, useEffect, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import { AiBrand, ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK, ATLASSIAN_AI_TROUBLESHOOT_LINK, ATLASSIAN_AI_USAGE_LIMITS_LINK, ATLASSIAN_HIPAA_COMPLICANCE_LINK, ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK } from '../../../constants';
import { messages } from '../../../messages';
import { DefinitionError } from '../../../v2/definitions/body/error/definition-error';
import { useAIAnswerContext } from '../../ai-answer-context';
import { NLPSearchErrorState } from '../../types';
import { AIAnswerResultSection, InfoLink } from './styled';
export var AIAnswerError = function AIAnswerError() {
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  var _useAIAnswerContext = useAIAnswerContext(),
    advancedSearchUrl = _useAIAnswerContext.advancedSearchUrl,
    _useAIAnswerContext$c = _useAIAnswerContext.commonAttributes,
    errorState = _useAIAnswerContext$c.errorState,
    errorCode = _useAIAnswerContext$c.errorCode,
    isReadingAids = _useAIAnswerContext.isReadingAids,
    brand = _useAIAnswerContext.brand;
  var _useAIEventsInstrumen = useAIEventsInstrumentation(),
    trackAIResultError = _useAIEventsInstrumen.trackAIResultError;
  useEffect(function () {
    if (errorState !== NLPSearchErrorState.Default) {
      trackAIResultError({
        aiErrorMessage: errorState || '',
        aiErrorCode: errorCode !== null && errorCode !== void 0 ? errorCode : 0
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  var brandName = brand === AiBrand.ROVO ? 'Rovo' : 'Atlassian Intelligence';
  var errorMessage = useMemo(function () {
    switch (errorState) {
      case NLPSearchErrorState.NoAnswer:
      case NLPSearchErrorState.NoAnswerSearchResults:
      case NLPSearchErrorState.NoAnswerHydration:
      case NLPSearchErrorState.NoAnswerRelevantContent:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_no_answer, {
          values: {
            brandName: brandName,
            learnMoreLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_onboarding_banner_tooltip_link))
          }
        }));
      case NLPSearchErrorState.NoAnswerKeywords:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_keyword, {
          values: {
            brandName: brandName,
            learnMoreLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_INTELLIGENCE_SEARCH_INFO_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_onboarding_banner_tooltip_link))
          }
        }));
      case NLPSearchErrorState.AcceptableUseViolation:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_acceptable_use, {
          values: {
            brandName: brandName,
            acceptableUseLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_ACCEPTABLE_USE_POLICY_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_component_error_state_acceptable_use_link))
          }
        }));
      case NLPSearchErrorState.SubjectiveQuery:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_subjective_query, {
          values: {
            brandName: brandName
          }
        }));
      case NLPSearchErrorState.NoAnswerOpenAIRateLimitUserAbuse:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_open_ai_rate_limit_error, {
          values: {
            brandName: brandName,
            supportLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_AI_USAGE_LIMITS_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_component_support_link))
          }
        }));
      case NLPSearchErrorState.NoAnswerWhoQuestion:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_no_who_is_answer, {
          values: {
            brandName: brandName
          }
        }));
      case NLPSearchErrorState.NetworkError:
      case NLPSearchErrorState.NoAnswerOpenAIResponseError:
      case NLPSearchErrorState.AIDisabled:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_network, {
          values: {
            brandName: brandName,
            supportLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_AI_TROUBLESHOOT_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_component_support_link))
          }
        }));
      case NLPSearchErrorState.HIPAAError:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_error_state_hipaa, {
          values: {
            supportLink: /*#__PURE__*/React.createElement(InfoLink, {
              href: ATLASSIAN_HIPAA_COMPLICANCE_LINK,
              target: '_blank'
            }, formatMessage(messages.ai_answer_component_hipaa_compliance_link))
          }
        }));
      case NLPSearchErrorState.Default:
      default:
        return /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, messages.ai_answer_component_result_error_text, {
          values: {
            brandName: brandName,
            advancedSearchLink: !!advancedSearchUrl ? /*#__PURE__*/React.createElement("a", {
              href: advancedSearchUrl
            }, formatMessage(messages.ai_answer_component_result_error_advanced_search_link)) : formatMessage(messages.ai_answer_component_result_error_advanced_search_link)
          }
        }));
    }
  }, [advancedSearchUrl, errorState, formatMessage, brandName]);
  return isReadingAids ? /*#__PURE__*/React.createElement(DefinitionError, null) : /*#__PURE__*/React.createElement(Fragment, null, /*#__PURE__*/React.createElement(AIAnswerResultSection, {
    isReadingAids: isReadingAids,
    isExpanded: true
  }, errorMessage));
};